import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { Link, createFileRoute } from '@tanstack/react-router'
import { FormattedMessage } from 'react-intl'
import { useAtomValue } from 'jotai'
import { TestIds } from '@/constants/testIds'
import { loggedInEmailAtom } from '@/state/authentication/login'
import { logUserAction } from '@/services/dashboard/logUserAction'
import { UserActivityAction } from '@/types/userActivity'
import type { UserActivityLogPayload } from '@/types/userActivity'

export const Route = createFileRoute('/privacy')({
  component: Privacy,
})

const DISCLOSURE_ITEMS = [
  'privacyPolicy.disclosure.item1',
  'privacyPolicy.disclosure.item2',
  'privacyPolicy.disclosure.item3',
  'privacyPolicy.disclosure.item4',
  'privacyPolicy.disclosure.item5',
  'privacyPolicy.disclosure.item6',
] as const

function Privacy() {
  useDocumentTitle('meta.title.privacyPolicy')
  const userId = useAtomValue(loggedInEmailAtom)

  const handleTermsOfUseClick = () => {
    const activityPayload: UserActivityLogPayload = {
      UserId: userId ?? undefined,
      Action: UserActivityAction.TermsOfUse,
    }
    logUserAction(activityPayload)
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 text-sm text-gray-800">
      <h2
        data-testid={TestIds.privacy.pageTitle}
        className="text-2xl font-bold mb-2"
      >
        <FormattedMessage id="privacyPolicy.title" />
      </h2>
      <hr className="border-0 h-1 w-12 bg-red-600 mb-6" />

      <section className="mb-6">
        <h3 className="text-lg font-bold mb-2">
          <FormattedMessage id="privacyPolicy.websiteTitle" />
        </h3>
        <p className="mb-4">
          <FormattedMessage id="privacyPolicy.website.paragraph1" />
        </p>
        <p className="mb-4">
          <FormattedMessage id="privacyPolicy.website.paragraph2" />
        </p>
        <p className="mb-0">
          <FormattedMessage id="privacyPolicy.website.paragraph3" />
        </p>
      </section>

      <section className="mb-6">
        <h3 className="text-lg font-bold mb-2">
          <FormattedMessage id="privacyPolicy.cookiesTitle" />
        </h3>
        <p className="mb-4">
          <FormattedMessage id="privacyPolicy.cookies.paragraph1" />
        </p>
        <p className="mb-4">
          <FormattedMessage
            id="privacyPolicy.cookies.paragraph2"
            values={{
              naiLink: (chunks) => (
                <a
                  href="https://www.networkadvertising.org/managing/opt_out.asp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        </p>
        <p className="mb-4">
          <FormattedMessage id="privacyPolicy.cookies.paragraph3" />
        </p>
        <p className="mb-0">
          <FormattedMessage id="privacyPolicy.cookies.paragraph4" />
        </p>
      </section>

      <section className="mb-6">
        <h3 className="text-lg font-bold mb-2">
          <FormattedMessage id="privacyPolicy.purchasingTitle" />
        </h3>
        <p className="mb-0">
          <FormattedMessage id="privacyPolicy.purchasing.paragraph1" />
        </p>
      </section>

      <section className="mb-6">
        <h3 className="text-lg font-bold mb-2">
          <FormattedMessage id="privacyPolicy.disclosureTitle" />
        </h3>
        <p className="mb-4">
          <FormattedMessage id="privacyPolicy.disclosure.intro" />
        </p>
        <ol className="list-decimal pl-5 space-y-2 mb-4">
          {DISCLOSURE_ITEMS.map((id) => (
            <li key={id}>
              <FormattedMessage id={id} />
            </li>
          ))}
        </ol>
        <p className="mb-4">
          <FormattedMessage id="privacyPolicy.disclosure.paragraph1" />
        </p>
        <p className="mb-4">
          <FormattedMessage id="privacyPolicy.disclosure.paragraph2" />
        </p>
        <p className="mb-0">
          <FormattedMessage id="privacyPolicy.disclosure.paragraph3" />
        </p>
      </section>

      <section className="mb-6">
        <h3 className="text-lg font-bold mb-2">
          <FormattedMessage id="privacyPolicy.linksTitle" />
        </h3>
        <p className="mb-4">
          <FormattedMessage id="privacyPolicy.links.paragraph1" />
        </p>
        <p className="mb-0">
          <FormattedMessage id="privacyPolicy.links.paragraph2" />
        </p>
      </section>

      <section className="mb-6">
        <h3 className="text-lg font-bold mb-2">
          <FormattedMessage id="privacyPolicy.childrenTitle" />
        </h3>
        <p className="mb-0">
          <FormattedMessage id="privacyPolicy.children.paragraph1" />
        </p>
      </section>

      <section className="mb-6">
        <h3 className="text-lg font-bold mb-2">
          <FormattedMessage id="privacyPolicy.consentTitle" />
        </h3>
        <p className="mb-0">
          <FormattedMessage id="privacyPolicy.consent.paragraph1" />
        </p>
      </section>

      <section className="mb-6">
        <h3 className="text-lg font-bold mb-2">
          <FormattedMessage id="privacyPolicy.termsTitle" />
        </h3>
        <p className="mb-0">
          <FormattedMessage
            id="privacyPolicy.terms.paragraph1"
            values={{
              termsLink: (chunks) => (
                <span data-testid={TestIds.privacy.termsLink}>
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                    onClick={handleTermsOfUseClick}
                  >
                    {chunks}
                  </Link>
                </span>
              ),
            }}
          />
        </p>
      </section>

      <p className="mb-2">
        <FormattedMessage id="privacyPolicy.effectiveDate" />
      </p>
      <p className="font-bold">
        <FormattedMessage id="privacyPolicy.footer" />
      </p>
    </div>
  )
}
